import React, { useState } from 'react';
import { 
    TextField, 
    Button, 
    styled, 
    Typography, 
    Box,
    Alert,
    CircularProgress,
    Paper,
    Divider,
    Grid
} from '@mui/material';
import Sidebar from '../components/Sidebar';
import config from '../config';
import { useNavigate } from 'react-router-dom';

const ContentContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
});

const FormContainer = styled('form')({
    display: 'flex',
    marginTop: '2rem',
    width: "100%",
    flexDirection: 'column',
    alignItems: 'stretch',
});

const Header = styled('h1')(({ theme }) => ({
    fontSize: '1.65rem',
    width: '100%',
    fontWeight: 'normal',
    fontFamily: theme.typography.fontFamily,
    color: '#2E2E2E',
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const FormElement = styled('div')({
    margin: "1rem 0"
});

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    height: '100%',
});

const FileUploadSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: '#fafafa',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: '#f0f0f0',
        borderColor: theme.palette.primary.dark,
    }
}));

const VideoUploadPage = () => {
    const [productId, setProductId] = useState('');
    const [wristRollVideo, setWristRollVideo] = useState(null);
    const [video360, setVideo360] = useState(null);
    const [pomVideos, setPomVideos] = useState([]);
    const [staticImages, setStaticImages] = useState([]);
    const [wristRollSize, setWristRollSize] = useState(6.5);
    const [wristSize360, setWristSize360] = useState(65.0);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const navigate = useNavigate();

    const handleVideoFileSelect = (file, type) => {
        if (file && file.type.startsWith('video/')) {
            if (type === 'wristRoll') {
                setWristRollVideo(file);
            } else if (type === '360') {
                setVideo360(file);
            }
        } else {
            setMessage({ type: 'error', text: 'Please select a valid video file.' });
        }
    };

    const handlePomVideoFileSelect = (files) => {
        const validFiles = Array.from(files).filter(file => 
            file.type.startsWith('video/')
        );
        
        if (validFiles.length !== files.length) {
            setMessage({ type: 'error', text: 'Some files are not valid videos. Only video files are allowed.' });
        }
        
        setPomVideos(validFiles);
    };

    const handleImageFileSelect = (files) => {
        const validFiles = Array.from(files).filter(file => 
            file.type.startsWith('image/') || file.type === 'image/webp'
        );
        
        if (validFiles.length !== files.length) {
            setMessage({ type: 'error', text: 'Some files are not valid images. Only image files are allowed.' });
        }
        
        setStaticImages(validFiles);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!productId.trim()) {
            setMessage({ type: 'error', text: 'Please enter a product ID.' });
            return;
        }

        if (!wristRollVideo && !video360 && pomVideos.length === 0 && staticImages.length === 0) {
            setMessage({ type: 'error', text: 'Please select at least one video file or image.' });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const formData = new FormData();

            if (wristRollVideo) {
                formData.append('wrist_roll_video', wristRollVideo);
            }
            if (video360) {
                formData.append('video_360', video360);
            }
            pomVideos.forEach((video, index) => {
                formData.append('pom_videos', video);
            });
            staticImages.forEach((image, index) => {
                formData.append('static_images', image);
            });

            const response = await fetch(`${config.serverUrl}/upload-video-content?product_id=${parseInt(productId)}&wrist_roll_size=${wristRollSize}&wrist_size_360=${wristSize360}`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({ 
                    type: 'success', 
                    text: `Video content uploaded successfully! JSON URL: ${data.json_url}` 
                });
                
                // Reset form
                setProductId('');
                setWristRollVideo(null);
                setVideo360(null);
                setPomVideos([]);
                setStaticImages([]);
                setWristRollSize(6.5);
                setWristSize360(65.0);
            } else {
                throw new Error(data.detail || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setMessage({ type: 'error', text: error.message || 'Upload failed. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const renderVideoUpload = (type, file, setFile) => {
        const labels = {
            wristRoll: 'Wrist Roll Video',
            '360': '360° Video',
            pom: 'POM Video'
        };
        
        const descriptions = {
            wristRoll: 'Upload a video showing the product on a wrist (saved frame by frame)',
            '360': 'Upload a 360° rotation video of the product (saved frame by frame)',
            pom: 'Upload a POM video (uploaded as video + thumbnail)'
        };

        return (
            <FileUploadSection>
                <Typography variant="h6" gutterBottom>
                    {labels[type]} 
                    {file && <span style={{ color: 'green' }}> ✓</span>}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {descriptions[type]}
                </Typography>
                <input
                    accept="video/*"
                    style={{ display: 'none' }}
                    id={`${type}-video-upload`}
                    type="file"
                    onChange={(e) => handleVideoFileSelect(e.target.files[0], type)}
                />
                <label htmlFor={`${type}-video-upload`}>
                    <Button
                        variant="outlined"
                        component="span"
                        disabled={loading}
                        sx={{ mt: 1 }}
                    >
                        {file ? file.name : `Select ${labels[type]}`}
                    </Button>
                </label>
                {file && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            File: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                        <Button
                            size="small"
                            color="error"
                            onClick={() => setFile(null)}
                            disabled={loading}
                            sx={{ mt: 0.5 }}
                        >
                            Remove
                        </Button>
                    </Box>
                )}
            </FileUploadSection>
        );
    };

    const renderPomVideosUpload = () => (
        <FileUploadSection>
            <Typography variant="h6" gutterBottom>
                POM Videos 
                {pomVideos.length > 0 && <span style={{ color: 'green' }}> ✓ ({pomVideos.length})</span>}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Upload one or more POM videos (uploaded as video + thumbnail)
            </Typography>
            <input
                accept="video/*"
                style={{ display: 'none' }}
                id="pom-videos-upload"
                type="file"
                multiple
                onChange={(e) => handlePomVideoFileSelect(e.target.files)}
            />
            <label htmlFor="pom-videos-upload">
                <Button
                    variant="outlined"
                    component="span"
                    disabled={loading}
                    sx={{ mt: 1 }}
                >
                    {pomVideos.length > 0 ? `${pomVideos.length} videos selected` : 'Select POM Videos'}
                </Button>
            </label>
            {pomVideos.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {pomVideos.map((video, index) => (
                        <Typography key={index} variant="body2" color="textSecondary">
                            {index + 1}. {video.name} ({(video.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                    ))}
                    <Button
                        size="small"
                        color="error"
                        onClick={() => setPomVideos([])}
                        disabled={loading}
                        sx={{ mt: 0.5 }}
                    >
                        Remove All
                    </Button>
                </Box>
            )}
        </FileUploadSection>
    );

    const renderStaticImagesUpload = () => (
        <FileUploadSection>
            <Typography variant="h6" gutterBottom>
                Static Images 
                {staticImages.length > 0 && <span style={{ color: 'green' }}> ✓ ({staticImages.length})</span>}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Upload one or more static images (uploaded as images)
            </Typography>
            <input
                accept="image/*"
                style={{ display: 'none' }}
                id="static-images-upload"
                type="file"
                multiple
                onChange={(e) => handleImageFileSelect(e.target.files)}
            />
            <label htmlFor="static-images-upload">
                <Button
                    variant="outlined"
                    component="span"
                    disabled={loading}
                    sx={{ mt: 1 }}
                >
                    {staticImages.length > 0 ? `${staticImages.length} images selected` : 'Select Static Images'}
                </Button>
            </label>
            {staticImages.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {staticImages.map((image, index) => (
                        <Typography key={index} variant="body2" color="textSecondary">
                            {index + 1}. {image.name} ({(image.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                    ))}
                    <Button
                        size="small"
                        color="error"
                        onClick={() => setStaticImages([])}
                        disabled={loading}
                        sx={{ mt: 0.5 }}
                    >
                        Remove All
                    </Button>
                </Box>
            )}
        </FileUploadSection>
    );

    return (
        <PageContainer>
            <Sidebar />
            <ContentContainer>
                <Header>Upload Video Content</Header>
                
                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 2 }}>
                        {message.text}
                    </Alert>
                )}

                <FormContainer onSubmit={handleSubmit}>
                    <FormElement>
                        <TextField
                            label="Product ID"
                            type="number"
                            value={productId}
                            onChange={(e) => setProductId(e.target.value)}
                            required
                            disabled={loading}
                            fullWidth
                            placeholder="Enter product ID"
                        />
                    </FormElement>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Video Files
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Upload one or more video files. At least one video or image is required.
                    </Typography>

                    {renderVideoUpload('wristRoll', wristRollVideo, setWristRollVideo)}
                    {renderVideoUpload('360', video360, setVideo360)}
                    {renderPomVideosUpload()}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Static Images
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Upload one or more static images (optional).
                    </Typography>

                    {renderStaticImagesUpload()}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Video Settings
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Wrist Roll Size (mm)"
                                type="number"
                                value={wristRollSize}
                                onChange={(e) => setWristRollSize(parseFloat(e.target.value))}
                                inputProps={{ step: 0.1, min: 0 }}
                                disabled={loading}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="360° Video Wrist Size (mm)"
                                type="number"
                                value={wristSize360}
                                onChange={(e) => setWristSize360(parseFloat(e.target.value))}
                                inputProps={{ step: 0.1, min: 0 }}
                                disabled={loading}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || (!wristRollVideo && !video360 && pomVideos.length === 0 && staticImages.length === 0) || !productId.trim()}
                        sx={{ mt: 2, py: 1.5 }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Upload Video Content'
                        )}
                    </Button>
                </FormContainer>
            </ContentContainer>
        </PageContainer>
    );
};

export default VideoUploadPage;
